"use client";

import type { ReactNode } from "react";
import { ImageBlock } from "@/cms/render/ImageBlock";
import { renderRichText } from "@/cms/render/Renderer";
import {
  HeadingBlock,
  ImageBlockType,
  ParagraphBlock,
  findBlock,
  findImageByVariant,
  findParagraphByVariant,
} from "./blockUtils";
import type { ContentTeaserComponent } from "./types";
import { EditSVG } from "@/components/ui/SVG";
import ArticlePageLink from "@/components/ui/ArticlePageLink";

export const ArticleTeaser: ContentTeaserComponent = ({
  blocks,
  locale,
  contentId,
  onRequestEdit,
  showEditButton,
  pageUrl,
}) => {
  const heading = findBlock(blocks, "heading") as HeadingBlock | undefined;
  const heroImage =
    findImageByVariant(blocks, "hero") ||
    (findBlock(blocks, "image") as ImageBlockType | undefined);
  const shortDescription = findParagraphByVariant(
    blocks,
    "shortDescription",
  ) as ParagraphBlock | undefined;

  let shortDescriptionNode: ReactNode | null = null;
  if (shortDescription?.data?.richText) {
    shortDescriptionNode = renderRichText(shortDescription.data.richText, {
      locale,
    });
  } else if (shortDescription?.data?.text) {
    shortDescriptionNode = (
      <p className="text-sm text-gray-600">{shortDescription.data.text}</p>
    );
  }

  if (!heading && !heroImage && !shortDescriptionNode) {
    return null;
  }

  const canShowEdit = Boolean(showEditButton && contentId && onRequestEdit);

  return (
    <article className="space-y-3">
      {heroImage ? (
        <div className="relative overflow-hidden rounded-lg">
          <ImageBlock
            data={heroImage.data}
            locale={locale}
            className="h-56 w-full rounded-lg object-cover"
          />
        </div>
      ) : null}
      <div className="space-y-2">
        {heading ? (
          <div className="flex items-center gap-2">
            {canShowEdit ? (
              <button
                type="button"
                onClick={(event) => {
                  event.stopPropagation();
                  onRequestEdit!(contentId!);
                }}
                className="inline-flex h-7 w-7 items-center justify-center rounded-md border border-gray-200 bg-white"
                aria-label="Edit content"
                title="Edit content"
              >
                <EditSVG
                  className="h-5 w-5"
                  svgColor="pinkCustom"
                  accentColor="var(--color-gray-500)"
                />
              </button>
            ) : null}
            <h2 className="text-xl font-semibold text-gray-900">
              {heading.data.text}
            </h2>
          </div>
        ) : null}
        {shortDescriptionNode}
        <ArticlePageLink href={pageUrl || null} locale={locale} className="w-full sm:w-auto" />
      </div>
    </article>
  );
};
